import { useMemo, useState } from "react";
import { Box, Button, Dialog, Typography, styled, useTheme } from "@mui/material";
import { Package, MapPin, Boxes, Check } from "lucide-react";
import { EntityThumb } from "@components/EntityThumb/EntityThumb";
import { FieldCard } from "@components/form/FieldCard";
import { FormFooter } from "@components/form/FormFooter";
import { InfoCallout } from "@components/form/InfoCallout";
import { PickerSheet, PickerOption } from "@components/form/PickerSheet";
import { usePrimaryEntityImages } from "@queries/useEntityImages";
import { tokens } from "@styles/tokens";
import { useStockForm } from "../../../hooks/useStockForm";
import { CreateProductDrawer } from "./CreateProductDrawer";
import { CreateLocationDrawer } from "./CreateLocationDrawer";

type StockFormProps = { handleClose: () => void };

type PickerKind = "product" | "location" | "status" | null;

/**
 * Create/Edit stock drawer, on the redesigned forms pattern: labelled field
 * cards, tap-to-open picker sheets for Product/Location/Status, a quantity
 * input, a review callout, and the shared sticky footer. All state + the save/
 * merge/backorder logic live in `useStockForm`; this renders and wires pickers.
 */
export function StockForm({ handleClose }: StockFormProps) {
  const theme = useTheme();
  const {
    isEdit,
    productLocked,
    values,
    errors,
    submitting,
    set,
    products,
    locations,
    stages,
    selectedProduct,
    selectedLocation,
    selectedStatus,
    locationPath,
    submit,
    pendingMerge,
    confirmMerge,
    cancelMerge,
  } = useStockForm();

  const [picker, setPicker] = useState<PickerKind>(null);
  const [createProduct, setCreateProduct] = useState(false);
  const [createLocation, setCreateLocation] = useState(false);

  // Thumbnail for the selected product only (cheap) — the picker list stays
  // text-only so it never fans out a request per product at scale.
  const { data: thumbs = {} } = usePrimaryEntityImages(
    "product",
    selectedProduct ? [selectedProduct.id] : []
  );

  const productOptions = useMemo<PickerOption[]>(
    () => products.map((p) => ({ value: p.id, label: p.name, sublabel: p.sku })),
    [products]
  );
  const locationOptions = useMemo<PickerOption[]>(
    () =>
      locations.map((l) => ({
        value: l.id,
        label: l.name,
        sublabel: l.path?.map((p) => p.name).join(" / "),
      })),
    [locations]
  );
  const statusDot = theme.skuey.brand[500];
  const statusOptions = useMemo<PickerOption[]>(
    () => stages.map((s) => ({ value: s.id, label: s.name, dotColor: statusDot })),
    [stages, statusDot]
  );

  return (
    <Container>
      <Scroll>
        <FieldCard
          label="Product"
          icon={
            selectedProduct ? (
              <EntityThumb
                url={thumbs[selectedProduct.id]}
                alt={selectedProduct.name}
                size={32}
              />
            ) : (
              <Package size={18} />
            )
          }
          onClick={productLocked ? undefined : () => setPicker("product")}
          disabled={productLocked}
          error={errors.product}
        >
          {selectedProduct ? (
            <Selected>
              <SelName>{selectedProduct.name}</SelName>
              {selectedProduct.sku && <SelSub>SKU: {selectedProduct.sku}</SelSub>}
            </Selected>
          ) : (
            <Placeholder>Select a product</Placeholder>
          )}
        </FieldCard>

        <FieldCard
          label="Location"
          icon={<MapPin size={18} />}
          onClick={() => setPicker("location")}
          error={errors.location}
        >
          {selectedLocation ? (
            <Selected>
              <SelName>{selectedLocation.name}</SelName>
              <SelSub>{locationPath(selectedLocation.id)}</SelSub>
            </Selected>
          ) : (
            <Placeholder>Select a location</Placeholder>
          )}
        </FieldCard>

        <FieldCard
          label="Quantity"
          icon={<Boxes size={18} />}
          trailing={<Units>units</Units>}
          helper={
            isEdit
              ? "Set the on-hand count for this location"
              : "Enter the number of units to add"
          }
          error={errors.quantity}
        >
          <QtyInput
            type="number"
            inputMode="numeric"
            min={0}
            value={values.quantity}
            onChange={(e) => set("quantity", Number(e.target.value))}
            aria-label="Quantity"
          />
        </FieldCard>

        <FieldCard
          label="Status"
          icon={<Dot style={{ backgroundColor: statusDot }} />}
          onClick={() => setPicker("status")}
        >
          {selectedStatus ? (
            <SelName>{selectedStatus.name}</SelName>
          ) : (
            <Placeholder>Select a status</Placeholder>
          )}
        </FieldCard>

        <InfoCallout title="Review before saving">
          {isEdit
            ? "This updates the on-hand count and inventory totals for this location."
            : "This adds stock to the selected location and updates inventory counts."}
        </InfoCallout>
      </Scroll>

      <FormFooter
        onCancel={handleClose}
        onSubmit={submit}
        submitting={submitting}
        submitLabel={isEdit ? "Save Changes" : "Save Stock"}
        submitIcon={<Check size={18} />}
      />

      <PickerSheet
        open={picker === "product"}
        title="Select Product"
        searchPlaceholder="Search products…"
        options={productOptions}
        selectedValue={values.product}
        onSelect={(v) => set("product", v)}
        onClose={() => setPicker(null)}
        actionLabel="New Product"
        onAction={() => setCreateProduct(true)}
      />
      <PickerSheet
        open={picker === "location"}
        title="Select Location"
        searchPlaceholder="Search locations…"
        options={locationOptions}
        selectedValue={values.location}
        onSelect={(v) => set("location", v)}
        onClose={() => setPicker(null)}
        actionLabel="New Location"
        onAction={() => setCreateLocation(true)}
      />
      <PickerSheet
        open={picker === "status"}
        title="Select Status"
        searchPlaceholder="Search statuses…"
        options={statusOptions}
        selectedValue={values.status ?? undefined}
        onSelect={(v) => set("status", v)}
        onClose={() => setPicker(null)}
      />

      <CreateProductDrawer
        open={createProduct}
        onClose={() => setCreateProduct(false)}
      />
      <CreateLocationDrawer
        open={createLocation}
        onClose={() => setCreateLocation(false)}
      />

      <Dialog open={!!pendingMerge} onClose={cancelMerge}>
        <Box sx={{ p: 3, maxWidth: 420 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Combine with existing entry?
          </Typography>
          {pendingMerge && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This location already has{" "}
              <strong>{pendingMerge.existing.quantity}</strong> of this item.
              Adding here combines them into{" "}
              <strong>{pendingMerge.existing.quantity + pendingMerge.addQty}</strong>.
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={cancelMerge}>
              Cancel
            </Button>
            <Button variant="contained" onClick={confirmMerge}>
              Combine
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Container>
  );
}

const Container = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
});

const Scroll = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(4),
  padding: tokens.space(4),
  backgroundColor: theme.skuey.surfaceAlt,
  "& > *": { flexShrink: 0 },
}));

const Selected = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
});

const SelName = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.ink,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const SelSub = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  color: theme.skuey.muted,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Placeholder = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.subtle,
}));

const Units = styled("span")(({ theme }) => ({
  flexShrink: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.subtle,
}));

const QtyInput = styled("input")(({ theme }) => ({
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.body[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.ink,
  // Hide the number spinners for a cleaner field.
  MozAppearance: "textfield",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
}));

const Dot = styled(Box)({
  width: 10,
  height: 10,
  borderRadius: tokens.radius.full,
});
